import { apiPaths } from 'api/kinerja/kepala-sekolah/config'
import { useMutationApi, useQueryApi } from 'utils/api'

import type { CustomError } from 'types/custom-error'
import type {
  ParamsBulkPenilaianPeriodikPegawai,
  ParamsGetPeriodikDetail,
  ParamsGetPeriodikList,
  ParamsGetPeriodikType,
  ResponseGetPeriodikDetail,
  ResponseGetPeriodikList,
  ResponseGetPeriodikType,
  ResponseGetPeriodikPegawaiDetail,
  ParamsGetPeriodikPegawaiDetail,
  ParamsBulkSendPeriodikToBkn,
  ParamsResetPenilaianPeriodik,
  KinerjaWithRole,
} from 'types/kinerja'
import type { UseMutationOptions, UseQueryOptions } from 'react-query'

export const API_GET_KS_PENILAIAN_PERIODIK_TYPE_QUERY_KEY =
  'API_GET_KS_PENILAIAN_PERIODIK_TYPE_QUERY_KEY'

export function useApiGetPenilaianPeriodikType({
  params,
  options,
}: {
  params: KinerjaWithRole<ParamsGetPeriodikType>
  options?: UseQueryOptions<ResponseGetPeriodikType>
}) {
  const { skpYear, tipe } = params

  return useQueryApi(`${apiPaths.guru}/penilaian/periodik/type`, {
    axiosConfig: {
      params: {
        skp_year: skpYear,
        tipe,
      },
    },
    queryConfig: {
      useErrorBoundary: false,
      ...options,
    },
    queryKey: [API_GET_KS_PENILAIAN_PERIODIK_TYPE_QUERY_KEY, skpYear, tipe],
  })
}

export const API_GET_PERIODIK_LIST_QUERY_KEY = 'API_GET_PERIODIK_LIST_QUERY_KEY'

export function useApiGetPeriodikList({
  params,
  options,
}: {
  params: ParamsGetPeriodikList
  options?: UseQueryOptions<ResponseGetPeriodikList>
}) {
  const { skpYear, ...restParams } = params

  return useQueryApi<ResponseGetPeriodikList>(
    `${apiPaths.guru}/penilaian/periodik`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          ...restParams,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_GET_PERIODIK_LIST_QUERY_KEY,
        { skpYear, npsn: params.npsn, tipe: params.tipe },
      ],
    }
  )
}

export const API_GET_PERIODIK_DETAIL_QUERY_KEY =
  'API_GET_PERIODIK_DETAIL_QUERY_KEY'

export function useApiGetPeriodikDetail({
  params,
  options,
}: {
  params: ParamsGetPeriodikDetail
  options?: UseQueryOptions<ResponseGetPeriodikDetail>
}) {
  const { skpYear, skpMonth, npsn } = params
  return useQueryApi<ResponseGetPeriodikDetail>(
    `${apiPaths.guru}/penilaian/periodik/detail`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_month: skpMonth,
          npsn,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_GET_PERIODIK_DETAIL_QUERY_KEY,
        { skpYear, skpMonth, npsn },
      ],
    }
  )
}

export const API_GET_PERIODIK_PEGAWAI_DETAIL_QUERY_KEY =
  'API_GET_PERIODIK_PEGAWAI_DETAIL_QUERY_KEY'

export function useApiGetPeriodikPegawaiDetail({
  params,
  options,
}: {
  params: ParamsGetPeriodikPegawaiDetail
  options?: UseQueryOptions<ResponseGetPeriodikPegawaiDetail>
}) {
  const { skpYear, skpMonth, ptkId } = params

  return useQueryApi<ResponseGetPeriodikPegawaiDetail>(
    `${apiPaths.guru}/penilaian/periodik/detail/${ptkId}`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_month: skpMonth,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_GET_PERIODIK_PEGAWAI_DETAIL_QUERY_KEY,
        { skpYear, skpMonth, ptkId },
      ],
    }
  )
}

export function useApiBulkPenilaianPeriodikPegawai(
  opts?: UseMutationOptions<
    unknown,
    CustomError,
    ParamsBulkPenilaianPeriodikPegawai
  >
) {
  return useMutationApi<
    unknown,
    CustomError,
    ParamsBulkPenilaianPeriodikPegawai
  >(`${apiPaths.guru}/penilaian/periodik/bulk`, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      ...opts,
    },
  })
}

export function useApiBulkSendPeriodikToBkn(
  opts?: UseMutationOptions<unknown, CustomError, ParamsBulkSendPeriodikToBkn>
) {
  return useMutationApi<unknown, CustomError, ParamsBulkSendPeriodikToBkn>(
    `${apiPaths.guru}/penilaian/periodik/send-bkn/bulk`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...opts,
      },
    }
  )
}

export function useApiResetPenilaianPeriodik(
  ptkId: string,
  opts?: UseMutationOptions<unknown, CustomError, ParamsResetPenilaianPeriodik>
) {
  return useMutationApi<unknown, CustomError, ParamsResetPenilaianPeriodik>(
    `${apiPaths.guru}/penilaian/periodik/reset/${ptkId}`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...opts,
      },
    }
  )
}
